/**
 * INSTITUTIONAL TELEMETRY FOR A SIMULATED TRADE — excursions, benchmark drift, alpha, and the
 * volatility-adjusted payoff.
 *
 * <h2>Why these live apart from `simulateTrade` and from `portfolioMetrics`</h2>
 *
 * <p>`simulateTrade` answers "what did this trade do"; `calculatePortfolioMetrics` answers "what
 * did a LIST of trades do" and is the one implementation of every figure it already provides.
 * What is here is neither: it is the per-trade telemetry an execution desk reads beside a P&L —
 * how far the trade went against you before it worked, how far in your favour before it gave
 * part back, and whether it beat simply holding over the identical bars.
 *
 * <p>Both the ENGINE'S theoretical trade and the reader's own MANUAL one need it, and they are
 * booked in different modules. A copy in each would be two definitions of MAE that agree on the
 * day they are written and never again, so there is one and both call it.
 *
 * <h2>⚠ THE BENCHMARK IS THE INSTRUMENT'S OWN BUY-AND-HOLD — a stated deviation</h2>
 *
 * <p>The brief asks for "excess return over SPY buy-and-hold during the exact t+1 to t+10 holding
 * period". What is implemented is excess over THIS INSTRUMENT'S buy-and-hold over exactly those
 * bars. Three reasons, and the first is the honest one:
 *
 * <ul>
 *   <li><b>A second series is not loaded.</b> Charting AAPL, the app holds AAPL's bars and
 *       nothing else; benchmarking against SPY would mean fetching and date-aligning a second
 *       EOD series, and in a replay bounding that series by the playhead too or the alpha itself
 *       becomes the leak this whole feature exists to prevent.</li>
 *   <li><b>On a SPY chart the two are IDENTICAL</b>, which is the case the brief names and the
 *       chart this engine is measured on.</li>
 *   <li><b>It is the question that judges the RULE.</b> "Did taking this signal beat holding the
 *       same instrument over the same bars" is what separates a trading edge from a drift
 *       harvest, and it is unanswerable from an index comparison alone.</li>
 * </ul>
 *
 * <p>So it is named `holdReturnPercent` and `alphaPercent` on the trade, and the card labels it
 * "vs buy-and-hold" rather than "vs SPY". A figure that claims a benchmark it did not use is
 * worse than one that names the benchmark it did.
 *
 * <h2>Alpha is ZERO for a long held to the horizon, and that is correct</h2>
 *
 * <p>Such a trade exits at the horizon bar's close, which is exactly where buy-and-hold ends, so
 * the excess is nil — taking it was equivalent to holding. The informative cases are the ones
 * that exit EARLY (a stop that saved or cost you the rest of the move) and every SHORT, where
 * beating a long-only benchmark means the instrument fell.
 */

import { BAR_CLOSE, BAR_HIGH, BAR_LOW } from './analyticaGeometry';

const n = (v) => (Number.isFinite(v) ? v : null);

/** Null-safe, so a partially-populated set reports `null` rather than a figure built from gaps. */
const mean = (values) => {
  const list = values.filter(Number.isFinite);
  return list.length === 0 ? null : list.reduce((a, b) => a + b, 0) / list.length;
};

export const EMPTY_EXCURSIONS = Object.freeze({
  maeAbs: null,
  maePercent: null,
  maeR: null,
  mfeAbs: null,
  mfePercent: null,
  mfeR: null,
  excursionBars: 0,
});

/**
 * MAXIMUM ADVERSE AND FAVOURABLE EXCURSION over the bars a position was held.
 *
 * <h2>Four decisions</h2>
 *
 * <ul>
 *   <li><b>Reported as POSITIVE MAGNITUDES.</b> "MAE 0.8R" is the desk convention and reads
 *       unambiguously; a signed MAE would be negative for a long and positive for a short under
 *       the same arithmetic, so the one number would mean opposite things by direction.</li>
 *   <li><b>From the bar AFTER the entry.</b> The entry fills at bar t's close, so t's own low is
 *       a price the position never existed at. Including it would charge every trade with the
 *       excursion of a bar it was not in.</li>
 *   <li><b>From HIGHS and LOWS, never closes.</b> The question is how far the trade went against
 *       you, and the close is where it ended the day rather than where it went.</li>
 *   <li><b>NEVER NEGATIVE.</b> A trade that only ever went up has an MAE of 0, not of minus
 *       something — zero adverse excursion is a real and good reading, and clamping says so
 *       where a negative number would read as an error.</li>
 * </ul>
 *
 * <p>`maeR` and `mfeR` are null when the trade carries no risk distance — a manual trade has no
 * stop, so it has no R, and inventing one from an ATR the reader never chose would put a figure
 * on the card that no decision of theirs produced.
 *
 * @param {Array<number[]>} ohlc  ECharts order
 * @param {number} entryIndex     the signal/open bar; the walk starts at the NEXT one
 * @param {number} exitIndex      inclusive; for an open position, the last revealed bar
 * @param {number} entryPrice
 * @param {number} sign           +1 long, -1 short
 * @param {number|null} risk      the entry-to-stop distance, or null
 */
export function excursionsOver(ohlc, entryIndex, exitIndex, entryPrice, sign, risk = null) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const from = Math.floor(n(entryIndex) ?? -1) + 1;
  const to = Math.floor(n(exitIndex) ?? -1);
  const entry = n(entryPrice);
  if (entry === null || entry === 0 || to < from) return { ...EMPTY_EXCURSIONS };

  const dir = sign < 0 ? -1 : 1;
  let worst = 0;
  let best = 0;
  let seen = 0;
  for (let k = from; k <= to && k < bars.length; k++) {
    const high = n(bars[k]?.[BAR_HIGH]);
    const low = n(bars[k]?.[BAR_LOW]);
    if (high === null || low === null) continue;
    seen += 1;
    // The favourable extreme is the HIGH for a long and the LOW for a short, and the adverse
    // one is its opposite. Both expressed as a move IN THE TRADE'S FAVOUR, then split by sign.
    const bestMove = (dir > 0 ? high - entry : entry - low);
    const worstMove = (dir > 0 ? low - entry : entry - high);
    if (bestMove > best) best = bestMove;
    if (worstMove < worst) worst = worstMove;
  }
  if (seen === 0) return { ...EMPTY_EXCURSIONS };

  const r = n(risk);
  const usable = r !== null && r > 0;
  const mae = Math.abs(Math.min(0, worst));
  const mfe = Math.max(0, best);
  return {
    maeAbs: mae,
    maePercent: (mae / entry) * 100,
    maeR: usable ? mae / r : null,
    mfeAbs: mfe,
    mfePercent: (mfe / entry) * 100,
    mfeR: usable ? mfe / r : null,
    excursionBars: seen,
  };
}

/**
 * The instrument's own buy-and-hold return over exactly the bars the position was held, in
 * percent of the entry close.
 *
 * <p>CLOSE to CLOSE, which is what makes it the benchmark rather than a second reading of the
 * trade: a trade that exits at its TARGET exits at a level, and the comparison is against where
 * the instrument actually finished on that same bar.
 *
 * @returns {number|null} null when either close is missing — never 0, which is a real reading
 */
export function holdReturnPercent(ohlc, entryIndex, exitIndex) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const a = n(bars[Math.floor(n(entryIndex) ?? -1)]?.[BAR_CLOSE]);
  const b = n(bars[Math.floor(n(exitIndex) ?? -1)]?.[BAR_CLOSE]);
  if (a === null || b === null || a === 0) return null;
  return ((b - a) / a) * 100;
}

/** The trade's return less the benchmark's over the same window. Null if either is absent. */
export function alphaOf(pnlPercent, holdPercent) {
  const p = n(pnlPercent);
  const h = n(holdPercent);
  return p === null || h === null ? null : p - h;
}

/**
 * Everything the excursion/benchmark pair contributes to a trade, in one call.
 *
 * <p>One function rather than three at each call site, so the engine and the manual booker
 * cannot end up carrying different subsets of the same telemetry.
 */
export function tradeTelemetry({
  ohlc, entryIndex, exitIndex, entryPrice, sign, risk = null, pnlPercent = null,
}) {
  const ex = excursionsOver(ohlc, entryIndex, exitIndex, entryPrice, sign, risk);
  const hold = holdReturnPercent(ohlc, entryIndex, exitIndex);
  return { ...ex, holdReturnPercent: hold, alphaPercent: alphaOf(pnlPercent, hold) };
}

export const EMPTY_INSTITUTIONAL = Object.freeze({
  alphaPercent: null,
  holdReturnPercent: null,
  alphaMeasured: 0,
  averageMaeR: null,
  averageMfeR: null,
  averageMaePercent: null,
  averageMfePercent: null,
  winnerAverageR: null,
  loserAverageR: null,
  payoffRatioR: null,
  excursionMeasured: 0,
});

/**
 * THE SESSION-LEVEL TELEMETRY, over realised trades only.
 *
 * <h2>Alpha is COMPOUNDED on both sides, and subtracted at the end</h2>
 *
 * <p>Not a mean of the per-trade alphas. The session's return is compounded — that is the one
 * implementation every other figure here uses — so the benchmark it is compared against has to
 * be compounded over the same windows or the difference is between two quantities measured
 * differently. `(trades − 1) − (hold − 1)` in growth factors, expressed in percent.
 *
 * <h2>The payoff ratio in R is the VOLATILITY-ADJUSTED one</h2>
 *
 * <p>`calculatePortfolioMetrics.payoffRatio` is the average win over the average loss in
 * PERCENT, so a win taken in a violent month and one taken in a quiet month count the same. R
 * is each trade's return divided by its own ATR-derived risk, so the same ratio computed in R
 * normalises every trade by the volatility it was actually taken in — which is the adjustment
 * the brief asks for, using the risk figure the engine already prices each trade with.
 *
 * <h2>Every figure is NULL rather than zero when its inputs are absent</h2>
 *
 * <p>A session of manual trades carries no R at all, and a mean of nothing is not zero. The
 * counts travel beside the figures so the panel can say `n/a` and say how many trades it had.
 */
export function institutionalTelemetry(trades) {
  const list = (Array.isArray(trades) ? trades : []).filter(Boolean);
  if (list.length === 0) return { ...EMPTY_INSTITUTIONAL };

  const withHold = list.filter(t => Number.isFinite(t.holdReturnPercent)
    && Number.isFinite(t.pnlPercent));
  const factor = (values) => values.reduce((f, v) => f * (1 + (v / 100)), 1);
  const tradeFactor = factor(withHold.map(t => t.pnlPercent));
  const holdFactor = factor(withHold.map(t => t.holdReturnPercent));

  const winners = list.filter(t => Number.isFinite(t.rMultiple) && t.rMultiple > 0);
  const losers = list.filter(t => Number.isFinite(t.rMultiple) && t.rMultiple < 0);
  const winnerAverageR = winners.length === 0 ? null : mean(winners.map(t => t.rMultiple));
  const loserAverageR = losers.length === 0 ? null : mean(losers.map(t => t.rMultiple));

  return {
    // NULL with nothing to compare, never 0 — zero alpha is "it matched the benchmark exactly".
    alphaPercent: withHold.length === 0 ? null : (tradeFactor - holdFactor) * 100,
    holdReturnPercent: withHold.length === 0 ? null : (holdFactor - 1) * 100,
    alphaMeasured: withHold.length,
    averageMaeR: mean(list.map(t => t.maeR)),
    averageMfeR: mean(list.map(t => t.mfeR)),
    averageMaePercent: mean(list.map(t => t.maePercent)),
    averageMfePercent: mean(list.map(t => t.mfePercent)),
    winnerAverageR,
    loserAverageR,
    // The denominator is a MAGNITUDE. Null with no losers — an absent denominator is not an
    // infinite payoff, the rule `profitFactorOf` already follows.
    payoffRatioR: (winnerAverageR === null || loserAverageR === null || loserAverageR === 0)
      ? null
      : winnerAverageR / Math.abs(loserAverageR),
    excursionMeasured: list.filter(t => Number.isFinite(t.maeR)).length,
  };
}

export default tradeTelemetry;
